import { ButtonBase, GridLegacy as Grid, Stack, styled } from '@mui/material'
import { PencilSimpleIcon, PlusIcon, XIcon } from '@phosphor-icons/react'
import { Alert, DisplayUserLink, FormTemplate, Modal, TinyButton, tokens, UserLinkList } from 'ComponentLibrary'
import { UsersAutocomplete } from 'ComponentLibrary/src/widgets/Participants/UserAutocomplete'
import { isEmpty } from 'lodash'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { Fragment, useContext, useEffect, useMemo } from 'react'
import { CustomerUser } from 'ComponentLibrary/src/common/users'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'

type CustomerUserAssignmentProps = {
    customerUsers: CustomerUser[]
    assignedCustomerUserIds: number[]
    open: boolean
    setOpen: (open: boolean) => void
    isPersistingAssignedCustomerUsers: boolean
    onSubmitCallback: (assignedCustomerUsers: number[]) => void
    modalPrimaryButtonLabel?: string
    copy?: Partial<CustomerUserAssignmentCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type CustomerUserAssignmentCopy = {
    addButton: string
    modalTitle: string
    cancel: string
    duplicateWarning: string
    assigneeLabel: string
    removeTooltip: string
    addMore: string
}

const DEFAULT_CUSTOMER_USER_ASSIGNMENT_COPY: CustomerUserAssignmentCopy = {
    addButton: 'Kund/in hinzufügen',
    modalTitle: 'Zuständige Kund/innen',
    cancel: 'Abbrechen',
    duplicateWarning: 'Dieselbe/r Kund/in wurde mehrfach ausgewählt. Bitte ändern Sie einen der Einträge.',
    assigneeLabel: 'Zuständige/r Kund/in',
    removeTooltip: 'Entfernen',
    addMore: 'Weitere hinzufügen',
}

const emptyUser: CustomerUser = {
    id: 0,
    uuid: '',
    email: '',
    pinned_threads: [],
    organization_name: '',
    role: 'customer',
    customer_uuid: '',
}

export function CustomerUserAssignment({
    customerUsers,
    assignedCustomerUserIds,
    open,
    setOpen,
    isPersistingAssignedCustomerUsers,
    onSubmitCallback,
    modalPrimaryButtonLabel = 'Übernehmen',
    copy,
    userLinkCopy,
}: CustomerUserAssignmentProps) {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const resolvedCopy = { ...DEFAULT_CUSTOMER_USER_ASSIGNMENT_COPY, ...copy }
    const assignedCustomerUsers = useMemo(
        () => customerUsers.filter((user) => assignedCustomerUserIds.includes(user.id)),
        [customerUsers, assignedCustomerUserIds]
    )
    const defaultValues = useMemo(() => (isEmpty(assignedCustomerUsers) ? [emptyUser] : assignedCustomerUsers), [assignedCustomerUsers])
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm<{ assignedCustomerUsers: CustomerUser[] }>({
        defaultValues: { assignedCustomerUsers: defaultValues },
    })

    useEffect(() => {
        reset({ assignedCustomerUsers: defaultValues })
    }, [reset, defaultValues])

    const watchedUsers = useWatch({ control, name: 'assignedCustomerUsers' })
    const hasDuplicateUsers = useMemo(() => {
        const userIds = watchedUsers.filter((user) => !!user && user.id !== 0).map((user) => user.id)
        return new Set(userIds).size !== userIds.length
    }, [watchedUsers])

    const onSubmit = handleSubmit((data) => {
        onSubmitCallback(data.assignedCustomerUsers.filter((user) => user.id !== 0).map((user) => user.id))
    })

    const onCancel = () => {
        const assignedCustomerUsers = customerUsers.filter((user) => assignedCustomerUserIds.includes(user.id))
        reset({ assignedCustomerUsers: isEmpty(assignedCustomerUsers) ? [emptyUser] : assignedCustomerUsers })
        setOpen(false)
    }

    const { fields, append, update } = useFieldArray({ control, name: 'assignedCustomerUsers' })

    return (
        <>
            {isEmpty(assignedCustomerUsers) ? (
                <TinyButton label={resolvedCopy.addButton} leftIcon={<PlusIcon />} onClick={() => setOpen(true)} />
            ) : (
                <StyledUserAssignmentButton onClick={() => setOpen(true)}>
                    <UserLinkList users={assignedCustomerUsers} size="medium" copy={userLinkCopy} />
                    <PencilSimpleIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
                </StyledUserAssignmentButton>
            )}
            <Modal
                isOpen={open}
                onClose={onCancel}
                title={resolvedCopy.modalTitle}
                width="700px"
                showLoading={isPersistingAssignedCustomerUsers}
                footerProps={{
                    primaryButton: {
                        text: modalPrimaryButtonLabel,
                        onClick: onSubmit,
                        disabled: isPersistingAssignedCustomerUsers || !isDirty || hasDuplicateUsers,
                    },
                    secondaryButton: { text: resolvedCopy.cancel, onClick: onCancel },
                }}
            >
                <Stack direction="column" gap={tokens.SizingSpacing300} width="100%">
                    {hasDuplicateUsers && (
                        <Alert size="small" severity="warning">
                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                {resolvedCopy.duplicateWarning}
                            </Typography>
                        </Alert>
                    )}
                    <FormTemplate onSubmit={onSubmit} onCancel={onCancel} isFormDirty={isDirty} hideFooter dense={usePiazzaInputs}>
                        {fields.map((user, index) => (
                            <Fragment key={user.id}>
                                <Grid item xs={5.5}>
                                    <Controller
                                        control={control}
                                        name={`assignedCustomerUsers.${index}`}
                                        render={({ field: { value } }) => (
                                            <UsersAutocomplete
                                                required
                                                users={customerUsers}
                                                value={value}
                                                label={resolvedCopy.assigneeLabel}
                                                userLinkCopy={userLinkCopy}
                                                onChange={(newUser) => {
                                                    update(index, (newUser as CustomerUser) ?? emptyUser)
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={5.5} style={{ alignItems: 'center', display: 'flex' }}>
                                    {user.uuid && <DisplayUserLink user={user} size="medium" copy={userLinkCopy} />}
                                </Grid>
                                <Grid item xs={1} style={{ alignItems: 'center', display: 'flex' }}>
                                    <IconButton
                                        icon={<XIcon />}
                                        tooltip={resolvedCopy.removeTooltip}
                                        size="small"
                                        variant="tertiary"
                                        onClick={() => update(index, emptyUser)}
                                    />
                                </Grid>
                            </Fragment>
                        ))}
                    </FormTemplate>
                    <TinyButton onClick={() => append(emptyUser)} label={resolvedCopy.addMore} leftIcon={<PlusIcon />} />
                </Stack>
            </Modal>
        </>
    )
}

const StyledUserAssignmentButton = styled(ButtonBase)(() => ({
    width: 'fit-content',
    borderRadius: tokens.SizingCornerS,
    padding: tokens.SizingSpacing100,
    gap: tokens.SizingSpacing150,
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
    '& *': {
        cursor: 'pointer',
    },
}))
